import React, { useState, Fragment, Suspense } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  XMarkIcon,
  ChartBarIcon,
  MagnifyingGlassIcon,
  ArrowRightOnRectangleIcon,
  BeakerIcon,
  CircleStackIcon,
  UserGroupIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useAuth } from '../contexts/AuthContext';
import { defaultGenomeBrowserPath, safeReturnPath } from '../services/authNavigation';
import VersionFooter from './VersionFooter';
import PageLoader, { PageErrorBoundary } from './PageLoader';

const ModernLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isAuthenticated, currentUser, logout, loading } = useAuth();
  const location = useLocation();
  const isJBrowseRoute = location.pathname.startsWith('/jbrowse');
  const signInReturnTo = safeReturnPath(
    location.pathname + location.search + location.hash,
    safeReturnPath(new URLSearchParams(location.search).get('returnTo'))
  );

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: ChartBarIcon },
    {
      name: 'Genome Browser',
      href: loading ? '/jbrowse' : defaultGenomeBrowserPath(isAuthenticated ? currentUser : null),
      icon: BeakerIcon,
    },
    { name: 'Data', href: '/data', icon: CircleStackIcon },
    { name: 'API & agents', href: '/integrations', icon: KeyIcon, requiresAuth: true },
    {
      name: 'BLAST Search',
      href: '/blast',
      icon: MagnifyingGlassIcon,
    },
    {
      name: 'Administration',
      href: '/admin',
      icon: UserGroupIcon,
      requiresAuth: true,
      capability: 'can_manage_roles',
    },
  ];

  const filteredNavigation = navigation.filter(
    item =>
      (!item.requiresAuth || (item.requiresAuth && isAuthenticated)) &&
      (!item.capability || currentUser?.capabilities?.[item.capability]) &&
      (!item.capabilities ||
        item.capabilities.every(capability => currentUser?.capabilities?.[capability]))
  );

  const isNavigationItemActive = item =>
    item.href.startsWith('/jbrowse') ? isJBrowseRoute : location.pathname === item.href;

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <a
        href="#main-content"
        onClick={event => {
          event.preventDefault();
          document.getElementById('main-content').focus();
        }}
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[2100] btn-primary btn-md"
      >
        Skip to content
      </a>
      {/* Mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 xl:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-2">
                    <button
                      type="button"
                      className="h-12 w-12 flex items-center justify-center"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>

                <div className="flex grow flex-col gap-y-6 overflow-y-auto bg-white px-6 pb-4">
                  <div className="flex h-16 shrink-0 items-center border-b border-gray-200">
                    <BeakerIcon className="h-8 w-8 text-gray-900" />
                    <span className="ml-2 text-xl font-bold text-gray-900">OikoBrowser</span>
                  </div>
                  <nav className="flex flex-1 flex-col" aria-label="Main navigation">
                    <ul className="flex flex-1 flex-col gap-y-8">
                      <li>
                        <ul className="-mx-2 space-y-2">
                          {filteredNavigation.map(item => (
                            <li key={item.name}>
                              <Link
                                to={item.href}
                                aria-current={isNavigationItemActive(item) ? 'page' : undefined}
                                onClick={() => setSidebarOpen(false)}
                                className={clsx(
                                  isNavigationItemActive(item)
                                    ? 'bg-gray-100 text-gray-900'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50',
                                  'group flex items-center min-h-12 gap-x-4 p-2 rounded-lg text-sm font-semibold leading-6'
                                )}
                              >
                                <item.icon
                                  className={clsx(
                                    isNavigationItemActive(item)
                                      ? 'text-gray-900'
                                      : 'text-gray-400 group-hover:text-gray-600',
                                    'h-6 w-6 shrink-0'
                                  )}
                                  aria-hidden="true"
                                />
                                {item.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </li>
                      {isAuthenticated && (
                        <li className="mt-auto">
                          <button
                            onClick={handleLogout}
                            className="group -mx-2 flex items-center min-h-12 gap-x-4 p-2 text-sm font-semibold leading-6 text-gray-600 hover:bg-gray-50 hover:text-gray-900 w-full"
                          >
                            <ArrowRightOnRectangleIcon
                              className="h-6 w-6 shrink-0 text-gray-400 group-hover:text-gray-600"
                              aria-hidden="true"
                            />
                            Sign out
                          </button>
                        </li>
                      )}
                    </ul>
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Top bar */}
      <div className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-xs sm:gap-x-6 sm:px-6 lg:px-8">
        <button
          type="button"
          className="h-12 w-12 shrink-0 flex items-center justify-center text-gray-700 xl:hidden"
          onClick={() => setSidebarOpen(true)}
        >
          <span className="sr-only">Open sidebar</span>
          <Bars3Icon className="h-6 w-6" aria-hidden="true" />
        </button>

        {/* Logo and navigation for desktop */}
        <div className="flex flex-1 items-center justify-between">
          <div className="flex items-center gap-x-6">
            <Link to="/" aria-label="OikoBrowser" className="flex items-center gap-x-2">
              <BeakerIcon className="h-8 w-8 text-gray-900" />
              <span className="hidden sm:inline text-xl font-bold text-gray-900">OikoBrowser</span>
            </Link>

            {/* Desktop navigation */}
            <nav className="hidden xl:flex xl:gap-x-2" aria-label="Main navigation">
              {filteredNavigation.map(item => (
                <Link
                  key={item.name}
                  to={item.href}
                  aria-current={isNavigationItemActive(item) ? 'page' : undefined}
                  className={clsx(
                    isNavigationItemActive(item)
                      ? 'bg-gray-100 text-gray-900 font-semibold'
                      : 'text-gray-600 hover:text-gray-900',
                    'inline-flex items-center min-h-12 px-4 rounded-lg text-sm font-medium transition-colors duration-150'
                  )}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* User menu */}
          <div className="flex items-center gap-x-4">
            {isAuthenticated ? (
              <>
                <span
                  className="hidden xl:block max-w-32 truncate text-sm text-gray-600"
                  title={currentUser?.username}
                >
                  {currentUser?.username}
                </span>
                <button onClick={handleLogout} className="btn-ghost btn-sm">
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link
                  to={`/login?returnTo=${encodeURIComponent(signInReturnTo)}`}
                  className="btn-secondary btn-sm"
                >
                  Sign in
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Page content */}
      <main id="main-content" tabIndex={-1} className="flex-1 w-full py-8 outline-none">
        <div className={isJBrowseRoute ? '' : 'mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-8'}>
          <div key={location.pathname}>
            <PageErrorBoundary key={location.pathname}>
              <Suspense fallback={<PageLoader />}>
                <Outlet />
              </Suspense>
            </PageErrorBoundary>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="mx-auto max-w-7xl px-6 py-4 lg:px-8">
          <div className="flex flex-col items-center justify-center gap-2">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} OikoBrowser. All rights reserved.
            </p>
            <VersionFooter />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ModernLayout;
